import {
  ReactNode,
  MouseEvent,
  ChangeEvent,
  useCallback,
  useState,
} from 'react';

import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import Chip from '@mui/material/Chip';
import classNames from 'classnames';

import { Box } from '../../Box';
import { CircularProgress } from '../../CircularProgress';
import { Label } from '../common';
import { Typography } from '../../Typography';
import { useStyles as useChipsStyles } from '../ChipsInput/ChipsInput.styles';

import { useRatingStyles } from './RatingInput.styles';
import { RatingInputItem } from './RatingInput.types';

/**
 * Свойства компонента {@link RatingInput}
 */
interface RatingInputProps {
  /**
   * Массив элементов рейтинга.
   */
  items: RatingInputItem[];

  /**
   * Текст метки для поля ввода.
   */
  label?: string;

  /**
   * Текущее значение рейтинга.
   */
  value: string | number | undefined;

  /**
   * Обработчик изменения значения.
   */
  onChange?: (value: string | number | undefined) => void;

  /**
   * Обработчик клика по чипу.
   */
  onClick?: () => void;

  /**
   * Флаг обязательности поля.
   *
   * @defaultValue `false`
   */
  required?: boolean;

  /**
   * Флаг ошибки валидации.
   */
  error?: boolean;

  /**
   * Текст подсказки при ошибке.
   */
  helperText?: string;

  /**
   * Всплывающая подсказка для метки.
   */
  tooltip?: ReactNode | null;

  /**
   * Флаг отключения компонента.
   *
   * @defaultValue `false`
   */
  disabled?: boolean;

  /**
   * Флаг загрузки данных.
   *
   * @defaultValue `false`
   */
  loading?: boolean;

  /**
   * Размер чипов.
   *
   * @defaultValue `'large'`
   */
  size?: 'large' | 'medium' | 'small' | 'extraSmall';

  /**
   * Размер чипов.
   *
   * @defaultValue `'small'`
   */
  iconSize?: 'medium' | 'small';

  /**
   * Направление расположения элементов.
   *
   * @defaultValue `'row'`
   */
  direction?: 'row' | 'column';

  /**
   * Флаг активности единственного варианта.
   *
   * @defaultValue `true`
   */
  activeSingleOption?: boolean;

  /**
   * Идентификатор для тестирования.
   */
  testId?: string;

  /**
   * Подсветка диапазона при наведении.
   *
   * @defaultValue `false`
   */
  highlightRange?: boolean;

  /**
   * Режим заполнения для рейтинговых компонентов.
   *
   * @defaultValue `true`
   */
  fillMode?: boolean;

  /**
   * Отображать метку под чипом.
   *
   * @defaultValue `false`
   */
  showLabelBelow?: boolean;

  /**
   * Максимальная ширина чипа.
   */
  chipMaxWidth?: number;
}

/**
 * Компонент для выбора рейтинга с помощью чипов.
 * Специализированная версия ChipsInput для рейтинговых систем.
 *
 * @public
 * @param label - Текст метки для поля ввода.
 * @param items - Массив элементов рейтинга.
 * @param value - Текущее значение рейтинга.
 * @param onChange - Обработчик изменения значения.
 * @param onClick - Обработчик клика по чипу.
 * @param required - Флаг обязательности поля.
 * @param error - Флаг ошибки валидации.
 * @param helperText - Текст подсказки при ошибке.
 * @param disabled - Флаг отключения компонента.
 * @param loading - Флаг загрузки данных.
 * @param tooltip - Всплывающая подсказка для метки.
 * @param size - Размер чипов.
 * @param direction - Направление расположения элементов.
 * @param activeSingleOption - Флаг активности единственного варианта.
 * @param testId - Идентификатор для тестирования.
 * @param highlightRange - Подсветка диапазона при наведении.
 * @param fillMode - Режим заполнения для рейтинговых компонентов.
 * @param showLabelBelow - Отображать метку под чипом.
 * @param chipMaxWidth - Максимальная ширина чипа.
 * @param iconSize - Размер чипов.
 * @returns Компонент с радио-группой чипов для выбора рейтинга.
 *
 * @remarks
 *
 * Ссылка на свойства компонента {@link RatingInputProps}
 */
export const RatingInput = ({
  label,
  items,
  value,
  onChange,
  onClick,
  required = false,
  error,
  helperText,
  disabled = false,
  loading = false,
  tooltip = null,
  size = 'large',
  iconSize = 'small',
  direction = 'row',
  activeSingleOption = true,
  testId,
  highlightRange = false,
  fillMode = true,
  showLabelBelow = false,
  chipMaxWidth,
  ...rest
}: RatingInputProps) => {
  const { classes: chipsClasses } = useChipsStyles();
  const { classes: ratingClasses } = useRatingStyles();

  const [hoveredValue, setHoveredValue] = useState<string | number | null>(
    null,
  );

  const handleChange = useCallback(
    ({ target: { value: inputValue } }: ChangeEvent<HTMLInputElement>) => {
      onChange?.(inputValue);
    },
    [onChange],
  );

  const handleChipClick = useCallback(
    (isActive: boolean) => (event: MouseEvent) => {
      if (isActive) {
        event.stopPropagation();
        event.preventDefault();
        onChange?.(undefined);
      }
      onClick?.();
    },
    [onChange, onClick],
  );

  const handleMouseEnter = useCallback(
    (itemValue: string | number) => () => {
      if (highlightRange) {
        setHoveredValue(itemValue);
      }
    },
    [highlightRange],
  );

  const handleMouseLeave = useCallback(() => {
    if (highlightRange) {
      setHoveredValue(null);
    }
  }, [highlightRange]);

  const getHighlightState = useCallback(
    (itemValue: string | number) => {
      if (!highlightRange || typeof itemValue !== 'number') {
        return { hover: false, selected: false };
      }

      const hasValue = value !== undefined && value !== null && value !== '';
      const numValue = typeof value === 'number' ? value : 0;
      const numHover = typeof hoveredValue === 'number' ? hoveredValue : 0;
      const isHovering = hoveredValue !== null;

      return {
        hover: isHovering && itemValue <= numHover,
        selected: hasValue && itemValue <= numValue,
      };
    },
    [highlightRange, hoveredValue, value],
  );

  return (
    <FormControl
      className={classNames(ratingClasses.root, 'aqa_rating_input')}
      component="fieldset"
    >
      {label && (
        <Label
          label={label}
          className="aqa_rating_input_label"
          required={required}
          tooltip={tooltip}
        />
      )}

      <RadioGroup
        row={direction === 'row'}
        data-testid={testId}
        value={value}
        onChange={handleChange}
        className={classNames(
          chipsClasses.row,
          fillMode && ratingClasses.flexRow,
          'aqa_rating_button',
        )}
      >
        {items.map(
          ({ label: itemLabel, value: itemValue, counter, color, icon }) => {
            const active =
              itemValue === value || (activeSingleOption && items.length === 1);
            const { hover: isHover, selected: isSelected } =
              getHighlightState(itemValue);

            let chipLabel;

            if (loading) {
              chipLabel = (
                <CircularProgress
                  size={20}
                  color={itemValue === value ? 'primary' : 'inherit'}
                />
              );
            }

            if (!loading && !showLabelBelow) {
              chipLabel = (
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  gap="8"
                  ml={icon && itemLabel ? '2' : '0'}
                >
                  {itemLabel}
                  {!!counter && (
                    <Box className={chipsClasses.counter}>{counter}</Box>
                  )}
                </Box>
              );
            }

            return (
              <Box
                key={itemValue}
                display="flex"
                flexDirection="column"
                alignItems="center"
                gap={showLabelBelow && itemLabel ? '12' : '0'}
                maxWidth={chipMaxWidth}
                width="100%"
              >
                <FormControlLabel
                  key={itemValue}
                  value={itemValue}
                  className={classNames(
                    chipsClasses.label,
                    fillMode && ratingClasses.flexLabel,
                  )}
                  disabled={disabled}
                  control={<Radio className={chipsClasses.radio} />}
                  label={
                    <Chip
                      {...rest}
                      onClick={handleChipClick(active)}
                      onMouseEnter={handleMouseEnter(itemValue)}
                      onMouseLeave={handleMouseLeave}
                      label={chipLabel}
                      variant="outlined"
                      disabled={disabled}
                      clickable={!disabled}
                      icon={!loading && icon ? <Box>{icon}</Box> : undefined}
                      className={classNames(
                        chipsClasses.chip,
                        chipsClasses[size],
                        ratingClasses.defaultRating,
                        active && chipsClasses.active,
                        isHover && ratingClasses.hover,
                        isSelected && ratingClasses.selected,
                        error && chipsClasses.error,
                        disabled && chipsClasses.disabled,
                        active && disabled && chipsClasses.activeDisabled,
                        color && chipsClasses[color],
                        ratingClasses[`iconSize-${iconSize}`],
                      )}
                    />
                  }
                />
                {showLabelBelow && itemLabel && (
                  <Typography variant="caption" color="text.light.secondary">
                    {itemLabel}
                  </Typography>
                )}
              </Box>
            );
          },
        )}
      </RadioGroup>

      {error && (
        <Box mt="4">
          <Typography variant="caption" color="accent.text.error">
            {helperText}
          </Typography>
        </Box>
      )}
    </FormControl>
  );
};
